import { Box, Card, Divider, Typography } from "@mui/material";
import TextField from "@mui/material/TextField";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import Button from "../../../common/components/Button";
import { toast } from "../../../common/components/Toast";
import { useAuth } from "../../../common/contexts/AuthContext";
import { DiscordLoginButton, GoogleLoginButton } from "../../components";
import styles from "./Login.module.css";

export default function Login() {
  const { t } = useTranslation("authentication");
  const [error, setError] = useState("");
  const [emailVerificationRequired, setEmailVerificationRequired] =
    useState(false);
  const [userEmail, setUserEmail] = useState("");
  const { login, authenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Redirect to home if already authenticated
  useEffect(() => {
    if (authenticated) {
      navigate("/");
    }
  }, [authenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setEmailVerificationRequired(false);
    setUserEmail("");

    const data = new FormData(e.target);
    const user = Object.fromEntries(data.entries());

    const response = await login(user);
    if (response.success) {
      toast.success(t("login.success"));
    } else {
      setError(response.message);
      if (response.email_verification_required) {
        setEmailVerificationRequired(true);
        setUserEmail(response.email || user.email);
      }
    }

    setLoading(false);
  };

  return (
    <div className={styles.loginPageWrapper}>
      {/* Animated background shapes */}
      <div className={styles.backgroundShapes}>
        <div className={`${styles.shape} ${styles.shape1}`}></div>
        <div className={`${styles.shape} ${styles.shape2}`}></div>
        <div className={`${styles.shape} ${styles.shape3}`}></div>
        <div className={`${styles.shape} ${styles.shape4}`}></div>
        <div className={`${styles.shape} ${styles.shape5}`}></div>
      </div>

      <Card
        style={{ maxWidth: 520 }}
        sx={{
          p: 4,
          boxShadow: 1,
          width: "100%",
          zIndex: 10,
          position: "relative",
        }}
      >
        <Typography variant="h2" sx={{ mb: 5, textAlign: "center" }}>
          {t("login.title")}
        </Typography>

        {/* OAuth Login Buttons */}
        <Box sx={{ mb: 3 }}>
          <GoogleLoginButton disabled={loading} />
        </Box>
        <Box sx={{ mb: 3 }}>
          <DiscordLoginButton disabled={loading} />
        </Box>

        {/* Divider */}
        <Box sx={{ display: "flex", alignItems: "center", my: 3 }}>
          <Divider sx={{ flex: 1 }} />
          <Typography variant="bodySm" sx={{ px: 2, color: "text.secondary" }}>
            {t("login.orDivider")}
          </Typography>
          <Divider sx={{ flex: 1 }} />
        </Box>

        {error && (
          <div className="mb-3">
            <span className="text-danger small">{error}</span>
            {emailVerificationRequired && (
              <div className="mt-2">
                <Link
                  to={`/verify-email${userEmail ? `?email=${encodeURIComponent(userEmail)}` : ""}`}
                  className="text-decoration-none"
                >
                  <Typography
                    variant="bodySm"
                    color="primary"
                    sx={{ fontWeight: 500 }}
                  >
                    {t("login.verifyEmailPrompt")}
                  </Typography>
                </Link>
              </div>
            )}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="py-2">
            <TextField
              label={t("login.email")}
              name="email"
              variant="outlined"
              fullWidth
              margin="normal"
              placeholder={t("login.emailPlaceholder")}
            />
          </div>
          <div className="py-2">
            <TextField
              label={t("login.password")}
              name="password"
              type="password"
              variant="outlined"
              fullWidth
              margin="normal"
              placeholder={t("login.passwordPlaceholder")}
            />
          </div>

          {/* Forgot Password Link */}
          <div className="d-flex justify-content-end py-2">
            <Link to="/forgot-password" className="text-decoration-none">
              <Typography
                variant="bodySm"
                color="primary"
                sx={{ fontSize: "0.875rem" }}
              >
                {t("login.forgotPassword")}
              </Typography>
            </Link>
          </div>

          <div className="py-2 mt-3">
            <Button variant="primary" size="lg" type="submit" loading={loading}>
              {t("login.submit")}
            </Button>
          </div>
        </form>

        {/* Sign up link */}
        <div className="d-flex justify-content-center align-items-center py-4">
          <Typography variant="bodyMd" className="px-2">
            {t("login.noAccount")}
          </Typography>
          <Link to="/register">
            <Typography variant="bodyMd" color="primary" fontWeight={500}>
              {t("login.signUp")}
            </Typography>
          </Link>
        </div>
      </Card>
    </div>
  );
}
